import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LegalDisclaimer } from '../../components/layout/LegalDisclaimer';
import { TopNav } from '../../components/layout/TopNav';
import {
  bracketsFromAgeIds,
  DEFAULT_AGE_SELECTION,
  DEFAULT_GIFT_AMOUNT_ID,
  STATE_FIPS_TO_NAME,
} from '../../config/dropdowns';
import { ApiError } from '../../services/apiClient';
import { mergeSx } from '../../theme';

import { ImpactMap } from './components/ImpactMap';
import { MapControls } from './components/MapControls';
import { MobileBottomSheet } from './components/MobileBottomSheet';
import { OverviewPanel, type AreaCard } from './components/OverviewPanel';
import { ProjectionsCard } from './components/ProjectionsCard';
import { amountToDollars } from './data/impactTypes';
import type { ImpactResult } from './data/impactTypes';
import { getAreaMetrics, getAreaName, loadLocationMetrics } from './services/impactService';
import { fetchImpactResult } from './services/impactApi';
import type { GeoLevel } from './types';
import { impactPageStyles as styles } from './ImpactPage.styles';

const MAX_AREAS = 5;

/** A selected geography — a state or a county, identified by FIPS. */
export interface SelectedArea {
  fips: string;
  name: string;
  level: GeoLevel;
}

export function ImpactPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [params, setParams] = useSearchParams();

  // Selections match the active `geoLevel` (states in state view, counties in
  // county view). Seed from `areas` (comma FIPS) + `level`, or the landing
  // page's single `location` FIPS.
  const [geoLevel, setGeoLevel] = useState<GeoLevel>(
    () => (params.get('level') as GeoLevel) || 'state',
  );
  const [selected, setSelected] = useState<SelectedArea[]>(() => {
    const lvl = (params.get('level') as GeoLevel) || 'state';
    const areasParam = params.get('areas');
    if (areasParam) {
      return areasParam
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean)
        .map((fips) => ({
          fips,
          name:
            lvl === 'county'
              ? (getAreaName('county', fips) ?? fips)
              : (STATE_FIPS_TO_NAME[fips] ?? fips),
          level: lvl,
        }));
    }
    const loc = params.get('location');
    const name = loc ? STATE_FIPS_TO_NAME[loc] : undefined;
    return loc && name ? [{ fips: loc, name, level: 'state' }] : [];
  });
  // The state FIPS whose counties are shown in county view (empty = all US).
  const [countyScope, setCountyScope] = useState<string[]>(() => {
    const s = params.get('scope');
    return s ? s.split(',').filter(Boolean) : [];
  });
  const [ageIds, setAgeIds] = useState<string[]>(() => {
    const age = params.get('age');
    return age ? age.split(',').filter(Boolean) : [...DEFAULT_AGE_SELECTION];
  });
  const [amountId, setAmountId] = useState<string>(
    params.get('amount') || DEFAULT_GIFT_AMOUNT_ID,
  );
  const [limitOpen, setLimitOpen] = useState(false);

  const amountDollars = useMemo(() => amountToDollars(amountId), [amountId]);
  const brackets = useMemo(() => bracketsFromAgeIds(ageIds), [ageIds]);

  // The live CDN dataset loads once at app start (see App.tsx); this just
  // gets us a handle on the same (idempotent) promise so area lookups below
  // recompute the moment it resolves, instead of staying stale.
  const [cdnReady, setCdnReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadLocationMetrics().then(() => {
      if (!cancelled) setCdnReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Per-area cards in the Overview Panel: identity (fips/name/level) always
  // comes from the real selection; `metrics` is real data or `null` — never
  // fabricated. Prefer whatever /impact/result returned for this FIPS (same
  // request as the aggregate below); else the live CDN dataset directly.
  const [liveAreas, setLiveAreas] = useState<ImpactResult['areas'] | null>(null);
  const areaCards: AreaCard[] = useMemo(() => {
    const apiByFips = new Map((liveAreas ?? []).map((a) => [a.code, a]));
    return selected.map((area) => {
      const metrics = apiByFips.get(area.fips) ?? getAreaMetrics(area.level, area.fips) ?? null;
      return {
        fips: area.fips,
        name: metrics?.name ?? area.name,
        level: area.level,
        metrics,
      };
    });
    // cdnReady isn't read directly but forces this to recompute once the
    // live dataset finishes loading (getAreaMetrics reads a module cache).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, liveAreas, cdnReady]);

  const [result, setResult] = useState<ImpactResult | null>(null);
  const [resultLoading, setResultLoading] = useState(true);
  const [resultError, setResultError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  // The real PIC result is the sole source for the aggregate stats + the
  // projection — there is no local fallback computation. A failed call
  // surfaces as an explicit error state, never a substitute number.
  useEffect(() => {
    let cancelled = false;
    setResultLoading(true);
    setResultError(null);

    fetchImpactResult({
      level: geoLevel,
      fips: selected.map((a) => a.fips),
      amountDollars,
      brackets,
    })
      .then((apiResult) => {
        if (cancelled) return;
        setResult(apiResult);
        setLiveAreas(apiResult.areas);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.warn('[ImpactPage] /impact/result failed:', err);
        setResult(null);
        setLiveAreas(null);
        setResultError(
          err instanceof ApiError ? err.message : 'Could not load impact data.',
        );
      })
      .finally(() => {
        if (!cancelled) setResultLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selected, geoLevel, amountDollars, brackets, retryToken]);

  const handleRetryResult = useCallback(() => setRetryToken((n) => n + 1), []);

  // Current-level selections highlighted on the map (states or counties).
  const selectedFips = useMemo(() => selected.map((a) => a.fips), [selected]);

  // Which states the map zooms to fit: the selected states in state view, or
  // the county scope in county view.
  const fitToStateFips = useMemo(
    () => (geoLevel === 'county' ? countyScope : selectedFips),
    [geoLevel, countyScope, selectedFips],
  );

  const handleToggleArea = useCallback(
    (area: SelectedArea) => {
      const exists = selected.some((a) => a.fips === area.fips && a.level === area.level);
      if (exists) {
        setSelected(selected.filter((a) => !(a.fips === area.fips && a.level === area.level)));
        return;
      }
      if (selected.length >= MAX_AREAS) {
        setLimitOpen(true);
        return;
      }
      // Drill-in: picking a county from the un-scoped (all-US) county view
      // scopes the view to that county's state and zooms into it.
      if (area.level === 'county' && geoLevel === 'county' && countyScope.length === 0) {
        setCountyScope([area.fips.slice(0, 2)]);
      }
      setSelected([...selected, area]);
    },
    [selected, geoLevel, countyScope],
  );

  const handleRemoveArea = useCallback((fips: string) => {
    setSelected((prev) => prev.filter((a) => a.fips !== fips));
  }, []);

  const handleClearAll = useCallback(() => setSelected([]), []);

  // Toggling the view:
  //  - State → County: scope the counties to the selected states, then clear
  //    the selection (the user now picks counties within that scope).
  //  - County → State: reset to the full-US state view (clean slate).
  const handleGeoLevel = useCallback(
    (next: GeoLevel) => {
      if (next === geoLevel) return;
      setCountyScope(next === 'county' ? selected.map((a) => a.fips) : []);
      setSelected([]);
      setGeoLevel(next);
    },
    [geoLevel, selected],
  );

  // Keep the URL in sync with the current selection + filters, so "Get Early
  // Access" carries the full context into the connect form (and the view is
  // deep-linkable).
  useEffect(() => {
    const next = new URLSearchParams();
    if (selected.length) next.set('areas', selected.map((a) => a.fips).join(','));
    if (countyScope.length) next.set('scope', countyScope.join(','));
    next.set('level', geoLevel);
    next.set('age', ageIds.join(','));
    next.set('amount', amountId);
    setParams(next, { replace: true });
  }, [selected, countyScope, geoLevel, ageIds, amountId, setParams]);

  return (
    <Box sx={styles.root}>
      <TopNav />

      {isMobile ? (
        <Box component="main" sx={styles.mobileMain}>
          <Box sx={styles.mobileControlsRow}>
            <MapControls
              geoLevel={geoLevel}
              onGeoLevel={handleGeoLevel}
              ageIds={ageIds}
              onAge={setAgeIds}
              amountId={amountId}
              onAmount={setAmountId}
              geoLevelVariant="select"
            />
          </Box>

          <Box sx={styles.mapRegion}>
            <ImpactMap
              geoLevel={geoLevel}
              selectedFips={selectedFips}
              countyScope={countyScope}
              fitToStateFips={fitToStateFips}
              onToggleArea={handleToggleArea}
              isMobile
            />

            <MobileBottomSheet
              result={result}
              resultLoading={resultLoading}
              resultError={resultError}
              onRetry={handleRetryResult}
              amountDollars={amountDollars}
              areaCards={areaCards}
              maxAreas={MAX_AREAS}
              onRemoveArea={handleRemoveArea}
              onClearAll={handleClearAll}
            />
          </Box>
        </Box>
      ) : (
        <Box component="main" sx={styles.main}>
          <Box sx={{ opacity: resultLoading ? 0.6 : 1, transition: 'opacity 200ms ease' }}>
            <OverviewPanel
              result={result}
              resultLoading={resultLoading}
              resultError={resultError}
              onRetry={handleRetryResult}
              areaCards={areaCards}
              maxAreas={MAX_AREAS}
              onRemoveArea={handleRemoveArea}
              onClearAll={handleClearAll}
            />
          </Box>

          <Box sx={styles.mapRegion}>
            <ImpactMap
              geoLevel={geoLevel}
              selectedFips={selectedFips}
              countyScope={countyScope}
              fitToStateFips={fitToStateFips}
              onToggleArea={handleToggleArea}
            />

            <Box sx={styles.controlsOverlay}>
              <MapControls
                geoLevel={geoLevel}
                onGeoLevel={handleGeoLevel}
                ageIds={ageIds}
                onAge={setAgeIds}
                amountId={amountId}
                onAmount={setAmountId}
              />
            </Box>

            <Box
              sx={mergeSx(styles.projectionsOverlay, {
                opacity: resultLoading ? 0.6 : 1,
                transition: 'opacity 200ms ease',
              })}
            >
              <ProjectionsCard result={result} resultError={resultError} amountDollars={amountDollars} />
            </Box>
          </Box>
        </Box>
      )}

      <LegalDisclaimer />

      <Snackbar
        open={limitOpen}
        autoHideDuration={3000}
        onClose={() => setLimitOpen(false)}
        message={`You can select up to ${MAX_AREAS} areas. Remove one to add another.`}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
